import React, { useState, useEffect, useRef } from "react";

import { storeEvent }                                                from "_analytics/index";
import { useGetProfileData }                                         from "_common/hooks/auth";
import { useSetUserOnBoardingAborted }                               from "_common/hooks/global";
import { useSetOpenUserOnboardingModal }                             from "_common/hooks/global";
import Toast                                                         from "_common/toast";
import { convertLanguageToLocale }                                   from "_common/utils/onboarding";
import { ROOTER_APP_STORE_LINK, ROOTER_PLAY_STORE_LINK }             from "_config/index";
import { getLanguages, getPopularOnboardingGames, setFavoriteGames } from "_services/api/broadcast";
import { onBoardUserProfile }                                        from "_services/api/user";

import Presentation from "./presentation";

const component = ( { onboardingStep, gamesSelected, fromUserSettings, updateContentLocale, updateFollowingGames } ) => {
	const [ step, setStep ]           = useState ( onboardingStep || 3 );
	const [ name, setName ]           = useState ( "" );
	const [ validName, setValidName ] = useState ( false );

	const [ languages, setLanguages ] = useState ( [] );
	const [ games, setGames ]         = useState ( [] );

	const initialRender             = useRef ( true );
	const setAborted                = useSetUserOnBoardingAborted ();
	const toggleUserOnboardingPopup = useSetOpenUserOnboardingModal ();

	const userProfile                         = useGetProfileData ();
	const [ locale, setLocale ]               = useState ( userProfile?.contentLocale );
	const [ selectedGames, setSelectedGames ] = useState ( userProfile?.followingGames?.map ( function ( game ) {
		return game["id"];
	} ) || [] );

	const [ selectedGameNames, setSelectedGameNames ] = useState ( [] );

	const [ userProfileDetails, setUserProfileDetails
	] = useState ( {
		...userProfile,
		name      : userProfile?.name,
		photo     : userProfile?.photo,
		city      : userProfile?.city,
		mobile    : userProfile?.mobile,
		shortBio  : userProfile?.shortBio,
		gender    : userProfile?.gender,
		instagram : ( userProfile?.instagram ) && ( "https://www.instagram.com/" + userProfile.instagram ),
		youtube   : userProfile?.youtube,
		discord   : userProfile?.discord
	}
	);

	useEffect ( () => {
		if ( gamesSelected ) {
			let gameIds = gamesSelected.map ( game => game.id );

			setSelectedGames ( gameIds );
		}
	}, [ gamesSelected ] );

	const getData = async () => {
		try {
			if ( step === 2 ) {
				const result = await getLanguages ();

				setLanguages ( result );
			} else if ( step === 3 ) {

				const games = await getPopularOnboardingGames ( 30 );

				setGames ( games );
			}
		} catch ( err ) {
			Toast.error ( err.displayMsg );
		}

	};

	useEffect ( () => {
		getData ();

	}, [ step ] );

	useEffect ( () => {
		setValidName ( name );
	}, [ name ] );

	const onBoardUser = async requestObj => {
		try {
			await onBoardUserProfile ( requestObj );

			setStep ( step + 1 );

		} catch ( err ) {
			Toast.error ( err.displayMsg );
		}
	};

	useEffect ( () => {
		if ( initialRender.current ) {
			initialRender.current = false;

		} else {

			if ( userProfileDetails.contentLocale.length !== 2 ) {
				let locale = convertLanguageToLocale ( userProfileDetails.contentLocale );

				    userProfileDetails.contentLocale = locale;
			}

			let requestObj = {};

			if ( step === 1 ) {
				requestObj = {
					id   : userProfileDetails.id,
					name : userProfileDetails.name
				};
			} else if ( step === 2 ) {
				requestObj = {
					id            : userProfileDetails.id,
					contentLocale : userProfileDetails.contentLocale
				};
			}

			onBoardUser ( requestObj );
		}

	}, [ userProfileDetails ] );

	const handleClickNext = async () => {

		if ( step === 1 ) {
			setUserProfileDetails ( {
				...userProfileDetails,
				name: name
			} );
			storeEvent ( "Profile_onboarding", "Name" );

		} else if ( step === 2 ) {

			setUserProfileDetails ( {
				...userProfileDetails,
				contentLocale: locale
			} );
			storeEvent ( "Profile_onboarding", "Language", locale );

		} else if ( step === 3 ) {

			try {
				await setFavoriteGames ( selectedGames );

				setStep ( step + 1 );

				storeEvent ( "Profile_onboarding", "Game", ` ${ JSON.stringify ( selectedGameNames ) } ` );

			} catch ( err ) {
				Toast.error ( err.displayMsg );
			}
		}

		// setStep ( step+1 );

	};

	const handleKeyPress = async event => {
		if ( event.key === "Enter" ) {
			if ( step === 1 && name && name.trim ().length !== 0 ) {
				setUserProfileDetails ( {
					...userProfileDetails,
					name: name
				} );
				storeEvent ( "Profile_onboarding", "Name" );
			} else if ( step === 2 && locale ) {

				setUserProfileDetails ( {
					...userProfileDetails,
					contentLocale: locale
				} );
				storeEvent ( "Profile_onboarding", "Language", locale );

			} else if ( step === 3 && selectedGames.length !== 0 ) {

				try {
					await setFavoriteGames ( selectedGames );

					setStep ( step + 1 );
					storeEvent ( "Profile_onboarding", "Game" );

				} catch ( err ) {
					Toast.error ( err.displayMsg );
				}
			}

		}
	};

	const handleLanguageSelection = language => {
		setLocale ( language );
	};

	const handleClickBack = () => {
		setStep ( step - 1 );
	};

	const handleGameSelection = ( value, gameName ) => {

		if ( selectedGames.includes ( value ) ) {
			setSelectedGames ( selectedGames.filter ( item => item !== value ) );
			setSelectedGameNames ( selectedGameNames.filter ( item => item !== gameName ) );
		} else {
			if ( selectedGames.length < 3 ) {
				setSelectedGames ( oldArray => [ ...oldArray, value ] );
				setSelectedGameNames ( oldArray => [ ...oldArray, gameName ] );
			}
		}
	};

	const handleClickDone = () => {
		if ( step === 2 ) {
			updateContentLocale ( locale );
		} else if ( step === 3 ) {
			updateFollowingGames ( selectedGames, games );
		}

		toggleUserOnboardingPopup ( false );
	};

	const handleClickCancel = () => {
		if ( ( step === 1 || step === 2 || step === 3 ) && !fromUserSettings ) {
			setAborted ( true );
		}
		toggleUserOnboardingPopup ( false );
	};

	const handleClickClose = () => {
		window.location.reload ();
		toggleUserOnboardingPopup ( false );
	};

	const handleClickDownload = () => {
		if ( navigator.userAgent.indexOf ( "Mac" ) != -1 ) {
			window.open ( ROOTER_APP_STORE_LINK, "_blank" );
		} else {
			window.open ( ROOTER_PLAY_STORE_LINK, "_blank" );
		}
	};

	return (
		<Presentation
			contentLocale                = { userProfile?.contentLocale }
			fromUserSettings             = { fromUserSettings }
			games                        = { games }
			handleClickBack              = { handleClickBack }
			handleClickCancel            = { handleClickCancel }
			handleClickClose             = { handleClickClose }
			handleClickDone              = { handleClickDone }
			handleClickDownload          = { handleClickDownload }
			handleClickNext              = { handleClickNext }
			handleGameSelection          = { handleGameSelection }
			handleKeyPress               = { handleKeyPress }
			handleLanguageSelection      = { handleLanguageSelection }
			languages                    = { languages }
			locale                       = { locale }
			name                         = { name }
			selectedGames                = { selectedGames }
			setName                      = { setName }
			setValidName                 = { setValidName }
			step                         = { step }
			validName                    = { validName }
		/>

	);
};

export default component;
